import { useState, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowRightLeft,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Copy,
  Download,
  Settings2,
  Plus,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn, formatCurrency, formatDate } from '@/lib/utils';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { getAccountSubscriptions } from '../api';
import {
  loadColumnVisibility,
  saveColumnVisibility,
  loadColumnFilters,
  saveColumnFilters,
} from '../lib/tableLocalStorage';
import ReassignDialog from './ReassignDialog';
import { CreateSubscriptionModal, SubscriptionFormModal, useSubscription, useUpdateSubscription } from '@/features/subscriptions';
import type { UpdateSubscriptionV4Input } from '@/features/subscriptions';
import type { SubscriptionDetail, AccountDashboardData } from '../types';

// =============================================================================
// Column Definition
// =============================================================================

interface ColumnDef<T> {
  key: string;
  label: string;
  defaultVisible?: boolean;
  sortKey?: string;
  filterType?: 'text' | 'date-range' | 'select' | 'number-range';
  filterOptions?: { label: string; value: string }[];
  getValue: (item: T) => string | number | null;
  render?: (item: T, helpers: SubscriptionRowHelpers) => React.ReactNode;
  align?: 'left' | 'center' | 'right';
  noFilter?: boolean;
  className?: string;
}

interface SubscriptionRowHelpers {
  onView: (contract: SubscriptionDetail) => void;
  onEdit: (contract: SubscriptionDetail) => void;
  onReassign: (contract: SubscriptionDetail) => void;
}

const STATUS_OPTIONS = [
  { label: 'Active', value: 'active' },
  { label: 'Draft', value: 'draft' },
  { label: 'Expired', value: 'expired' },
  { label: 'Cancelled', value: 'cancelled' },
];

const statusVariantMap: Record<string, 'success' | 'secondary' | 'outline' | 'destructive'> = {
  active: 'success',
  draft: 'secondary',
  expired: 'outline',
  cancelled: 'destructive',
};

const COLUMNS: ColumnDef<SubscriptionDetail>[] = [
  {
    key: 'contractNumber',
    label: 'Subscription #',
    sortKey: 'contractNumber',
    filterType: 'text',
    getValue: (c) => c.contractNumber,
    render: (c) => <span className="font-medium">{c.contractNumber}</span>,
  },
  {
    key: 'name',
    label: 'Name',
    sortKey: 'name',
    filterType: 'text',
    getValue: (c) => c.name,
  },
  {
    key: 'status',
    label: 'Status',
    filterType: 'select',
    filterOptions: STATUS_OPTIONS,
    getValue: (c) => c.status,
    render: (c) => <Badge variant={statusVariantMap[c.status] || 'secondary'}>{c.status}</Badge>,
  },
  {
    key: 'startDate',
    label: 'Start Date',
    sortKey: 'startDate',
    filterType: 'date-range',
    getValue: (c) => c.startDate,
    render: (c) => formatDate(c.startDate),
  },
  {
    key: 'endDate',
    label: 'End Date',
    sortKey: 'endDate',
    filterType: 'date-range',
    getValue: (c) => c.endDate,
    render: (c) => {
      const daysRemaining = c.endDate
        ? Math.ceil((new Date(c.endDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
        : null;
      return (
        <div className="flex items-center gap-2">
          {formatDate(c.endDate)}
          {c.status === 'active' && daysRemaining !== null && daysRemaining <= 90 && (
            <Badge variant={daysRemaining <= 30 ? 'destructive' : 'warning'} className="text-xs">
              {daysRemaining}d
            </Badge>
          )}
        </div>
      );
    },
  },
  {
    key: 'billingFrequency',
    label: 'Billing',
    sortKey: 'billingFrequency',
    defaultVisible: false,
    filterType: 'text',
    getValue: (c) => c.billingFrequency || '',
    render: (c) => c.billingFrequency || '—',
  },
  {
    key: 'autoRenew',
    label: 'Auto Renew',
    defaultVisible: false,
    filterType: 'select',
    filterOptions: [
      { label: 'Yes', value: 'true' },
      { label: 'No', value: 'false' },
    ],
    getValue: (c) => c.autoRenew === undefined ? '' : String(c.autoRenew),
    render: (c) => c.autoRenew === undefined ? '—' : c.autoRenew ? 'Yes' : 'No',
  },
  {
    key: 'totalValue',
    label: 'Value',
    sortKey: 'totalValue',
    filterType: 'number-range',
    getValue: (c) => parseFloat(c.totalValue) || 0,
    render: (c) => (
      <span className="font-medium">{formatCurrency(parseFloat(c.totalValue), c.currency)}</span>
    ),
    align: 'right',
  },
  {
    key: 'actions',
    label: '',
    noFilter: true,
    getValue: () => '',
    render: (c, { onView, onEdit, onReassign }) => (
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onView(c);
          }}
          className="text-atek-blue hover:underline text-sm cursor-pointer"
        >
          View
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onEdit(c);
          }}
          className="text-atek-blue hover:underline text-sm cursor-pointer"
        >
          Edit
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onReassign(c);
          }}
          className="text-muted-foreground hover:text-foreground cursor-pointer"
          title="Reassign to another account"
        >
          <ArrowRightLeft className="h-4 w-4" />
        </button>
      </div>
    ),
    align: 'right',
  },
];

// =============================================================================
// localStorage Persistence
// =============================================================================

const VISIBILITY_STORAGE_KEY = 'atek-account-contracts-column-visibility';
const FILTERS_STORAGE_KEY_PREFIX = 'atek-account-contracts-column-filters';
const LEGACY_GLOBAL_FILTERS_KEY = 'atek-account-contracts-column-filters';

function loadColumnFiltersWithLegacyFallback(accountId: string): Record<string, string> {
  const fromPerAccount = loadColumnFilters(FILTERS_STORAGE_KEY_PREFIX, accountId);
  if (Object.keys(fromPerAccount).length > 0) return fromPerAccount;
  try {
    const legacy = localStorage.getItem(LEGACY_GLOBAL_FILTERS_KEY);
    if (legacy) return JSON.parse(legacy);
  } catch { /* ignore */ }
  return {};
}

function getDefaultVisibility(): Record<string, boolean> {
  const defaults: Record<string, boolean> = {};
  for (const col of COLUMNS) {
    defaults[col.key] = col.defaultVisible !== false;
  }
  return defaults;
}

// =============================================================================
// CSV Export Utilities
// =============================================================================

function escapeCSVValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  let str = String(value);
  if (/^[=+\-@\t\r]/.test(str)) str = "'" + str;
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function exportToCSV(items: SubscriptionDetail[], columns: ColumnDef<SubscriptionDetail>[], filename: string) {
  const exportCols = columns.filter((c) => !c.noFilter);
  const header = exportCols.map((c) => escapeCSVValue(c.label)).join(',');
  const rows = items.map((item) =>
    exportCols.map((c) => escapeCSVValue(c.getValue(item))).join(','),
  );
  const csvContent = [header, ...rows].join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function copyToClipboard(items: SubscriptionDetail[], columns: ColumnDef<SubscriptionDetail>[]): Promise<void> {
  const exportCols = columns.filter((c) => !c.noFilter);
  const header = exportCols.map((c) => c.label).join('\t');
  const rows = items.map((item) =>
    exportCols.map((c) => String(c.getValue(item) ?? '')).join('\t'),
  );
  const text = [header, ...rows].join('\n');
  return navigator.clipboard.writeText(text);
}

// =============================================================================
// SortableHeader Component
// =============================================================================

function SortableHeader({
  label,
  sortKey,
  currentSortKey,
  sortDirection,
  onSort,
}: {
  label: string;
  sortKey: string;
  currentSortKey?: string;
  sortDirection?: 1 | -1;
  onSort: (key: string) => void;
}) {
  const isActive = currentSortKey === sortKey;
  return (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 hover:bg-transparent"
      onClick={() => onSort(sortKey)}
    >
      {label}
      {isActive ? (
        sortDirection === 1 ? <ArrowUp className="ml-1 h-3 w-3" /> : <ArrowDown className="ml-1 h-3 w-3" />
      ) : (
        <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />
      )}
    </Button>
  );
}

// =============================================================================
// Filter Row Renderer
// =============================================================================

function renderColumnFilter(
  col: ColumnDef<SubscriptionDetail>,
  columnFilters: Record<string, string>,
  setColumnFilter: (key: string, value: string) => void,
) {
  if (col.noFilter) return <TableHead key={`filter-${col.key}`} />;

  if (col.filterType === 'select' && col.filterOptions) {
    const selected: string[] = (() => {
      try { return columnFilters[col.key] ? JSON.parse(columnFilters[col.key]) : []; }
      catch { return []; }
    })();

    const toggle = (opt: string) => {
      const next = selected.includes(opt)
        ? selected.filter((o) => o !== opt)
        : [...selected, opt];
      setColumnFilter(col.key, next.length ? JSON.stringify(next) : '');
    };

    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-7 w-full text-xs justify-start font-normal">
              {selected.length ? `${selected.length} selected` : '—'}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-44">
            {col.filterOptions.map((opt) => (
              <DropdownMenuCheckboxItem
                key={opt.value}
                checked={selected.includes(opt.value)}
                onCheckedChange={() => toggle(opt.value)}
                onSelect={(e) => e.preventDefault()}
              >
                {opt.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableHead>
    );
  }

  if (col.filterType === 'date-range') {
    const raw = columnFilters[col.key] || '';
    const [from = '', to = ''] = raw.split('>>');
    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <div className="flex flex-col gap-0.5">
          <Input
            type="date"
            className="h-6 text-xs px-1"
            value={from}
            onChange={(e) => setColumnFilter(col.key, `${e.target.value}>>${to}`)}
            title="From"
          />
          <Input
            type="date"
            className="h-6 text-xs px-1"
            value={to}
            onChange={(e) => setColumnFilter(col.key, `${from}>>${e.target.value}`)}
            title="To"
          />
        </div>
      </TableHead>
    );
  }

  if (col.filterType === 'number-range') {
    const raw = columnFilters[col.key] || '';
    const [min = '', max = ''] = raw.split('>>');
    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <div className="flex flex-col gap-0.5">
          <Input
            type="number"
            className="h-6 text-xs px-1"
            value={min}
            onChange={(e) => setColumnFilter(col.key, `${e.target.value}>>${max}`)}
            placeholder="Min"
          />
          <Input
            type="number"
            className="h-6 text-xs px-1"
            value={max}
            onChange={(e) => setColumnFilter(col.key, `${min}>>${e.target.value}`)}
            placeholder="Max"
          />
        </div>
      </TableHead>
    );
  }

  return (
    <TableHead key={`filter-${col.key}`} className="p-1">
      <Input
        className="h-7 text-xs"
        value={columnFilters[col.key] || ''}
        onChange={(e) => setColumnFilter(col.key, e.target.value)}
        placeholder="..."
      />
    </TableHead>
  );
}

// =============================================================================
// SubscriptionsTab Component (Mode B: server-side pagination + client-side refinement)
// =============================================================================

interface SubscriptionsTabProps {
  accountId: string;
}

// Mirror SensorsTab / QuotesTab / InvoicesTab: fetch everything in one page so
// users scroll + filter client-side, no pagination footer.
const PAGE_SIZE = 5000;

export default function SubscriptionsTab({ accountId }: SubscriptionsTabProps) {
  const queryClient = useQueryClient();

  // Server-side state
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Client-side column filters (refinement within page)
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(
    () => loadColumnFiltersWithLegacyFallback(accountId),
  );
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(
    () => loadColumnVisibility(VISIBILITY_STORAGE_KEY, getDefaultVisibility()),
  );

  // Client-side sort (within page)
  const [sortKey, setSortKey] = useState<string | undefined>();
  const [sortDirection, setSortDirection] = useState<1 | -1>(1);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [reassignContract, setReassignContract] = useState<SubscriptionDetail | null>(null);
  const [editingContractId, setEditingContractId] = useState<string | null>(null);

  const dashboardData = queryClient.getQueryData<AccountDashboardData>(['account-dashboard', accountId]);
  const accountName = dashboardData?.account?.name || 'Current Account';

  // Persist column filters to localStorage
  useEffect(() => {
    saveColumnFilters(FILTERS_STORAGE_KEY_PREFIX, accountId, columnFilters);
  }, [columnFilters, accountId]);

  const setColumnFilter = (key: string, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const toggleColumn = (key: string) => {
    const updated = { ...columnVisibility, [key]: !columnVisibility[key] };
    setColumnVisibility(updated);
    saveColumnVisibility(VISIBILITY_STORAGE_KEY, updated);
  };

  const isVisible = (key: string) => columnVisibility[key] !== false;
  const visibleColumns = COLUMNS.filter((col) => isVisible(col.key));

  // Server-side data fetch
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['account-contracts', accountId, page, statusFilter],
    queryFn: () =>
      getAccountSubscriptions(accountId, {
        pageNumber: page,
        pageLength: PAGE_SIZE,
        status: statusFilter === 'all' ? undefined : statusFilter,
      }),
  });

  const contracts = data?.data || [];
  const total = data?.total || 0;

  // Client-side column filtering (AND-combined, within current page)
  const filteredItems = useMemo(() => {
    const activeFilters = Object.entries(columnFilters).filter(([, v]) => v);
    if (activeFilters.length === 0) return contracts;

    return contracts.filter((item) =>
      activeFilters.every(([colKey, filterValue]) => {
        const col = COLUMNS.find((c) => c.key === colKey);
        if (!col || col.noFilter) return true;

        const rawValue = col.getValue(item);

        if (col.filterType === 'date-range') {
          const [from, to] = filterValue.split('>>');
          const dateStr = String(rawValue || '');
          if (from && dateStr < from) return false;
          if (to && dateStr > to + 'T23:59:59') return false;
          return true;
        }

        if (col.filterType === 'select') {
          try {
            const selected = JSON.parse(filterValue) as string[];
            return selected.length === 0 || selected.includes(String(rawValue || ''));
          } catch { return true; }
        }

        if (col.filterType === 'number-range') {
          const [minStr, maxStr] = filterValue.split('>>');
          const num = Number(rawValue);
          if (isNaN(num)) return false;
          if (minStr && num < Number(minStr)) return false;
          if (maxStr && num > Number(maxStr)) return false;
          return true;
        }

        return String(rawValue ?? '').toLowerCase().includes(filterValue.toLowerCase());
      }),
    );
  }, [contracts, columnFilters]);

  // Client-side sorting (within filtered page)
  const sortedItems = useMemo(() => {
    if (!sortKey) return filteredItems;
    const col = COLUMNS.find((c) => c.sortKey === sortKey);
    if (!col) return filteredItems;

    return [...filteredItems].sort((a, b) => {
      const aVal = col.getValue(a);
      const bVal = col.getValue(b);
      if (aVal === null && bVal === null) return 0;
      if (aVal === null) return 1;
      if (bVal === null) return -1;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return (aVal - bVal) * sortDirection;
      }
      return String(aVal).localeCompare(String(bVal)) * sortDirection;
    });
  }, [filteredItems, sortKey, sortDirection]);

  const handleSort = (key: string) => {
    if (key === sortKey) {
      setSortDirection(sortDirection === 1 ? -1 : 1);
    } else {
      setSortKey(key);
      setSortDirection(1);
    }
  };

  // Subscription detail query & mutation for modal
  const editContractQuery = useSubscription(editingContractId);
  const updateMutation = useUpdateSubscription();

  const handleUpdateContract = async (updateData: UpdateSubscriptionV4Input) => {
    if (!editingContractId) return;
    await updateMutation.mutateAsync({ id: editingContractId, data: updateData });
    queryClient.invalidateQueries({ queryKey: ['account-contracts', accountId] });
    queryClient.invalidateQueries({ queryKey: ['account-dashboard', accountId] });
  };

  const handleContractCreated = (contractId: string) => {
    setEditingContractId(contractId);
    queryClient.invalidateQueries({ queryKey: ['account-contracts', accountId] });
    queryClient.invalidateQueries({ queryKey: ['account-dashboard', accountId] });
  };

  // Row helpers for column renderers
  const rowHelpers: SubscriptionRowHelpers = {
    onView: (c) => setEditingContractId(c.id),
    onEdit: (c) => setEditingContractId(c.id),
    onReassign: setReassignContract,
  };

  if (error) {
    return (
      <Card>
        <CardContent className="py-10 text-center space-y-4">
          <p className="text-destructive">Error loading contracts</p>
          <p className="text-sm text-muted-foreground">
            {error instanceof Error ? error.message : 'An unexpected error occurred'}
          </p>
          <Button variant="outline" onClick={() => refetch()}>
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Subscriptions</CardTitle>
            <CardDescription>
              {filteredItems.length < contracts.length
                ? `Showing ${filteredItems.length} of ${contracts.length} on this page`
                : `Showing ${contracts.length} of ${total}`}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[140px] h-8">
                <SelectValue placeholder="Filter status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                copyToClipboard(sortedItems, COLUMNS);
                toast.success('Copied to clipboard');
              }}
            >
              <Copy className="h-4 w-4 mr-1" />
              Copy
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                exportToCSV(sortedItems, COLUMNS, 'account-contracts');
                toast.success(`Exported ${sortedItems.length} rows`);
              }}
            >
              <Download className="h-4 w-4 mr-1" />
              CSV
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Settings2 className="h-4 w-4 mr-1" />
                  Columns
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {COLUMNS.filter((c) => !c.noFilter).map((col) => (
                  <DropdownMenuCheckboxItem
                    key={col.key}
                    checked={isVisible(col.key)}
                    onCheckedChange={() => toggleColumn(col.key)}
                    onSelect={(e) => e.preventDefault()}
                  >
                    {col.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button size="sm" onClick={() => setShowCreateModal(true)}>
              <Plus className="h-4 w-4 mr-1" />
              New Subscription
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              {/* ROW 1: Column filters (always visible) */}
              <TableRow className="bg-muted/30 hover:bg-muted/30">
                {visibleColumns.map((col) => renderColumnFilter(col, columnFilters, setColumnFilter))}
              </TableRow>

              {/* ROW 2: Column labels + sort */}
              <TableRow>
                {visibleColumns.map((col) => (
                  <TableHead key={col.key} className={cn(col.align === 'right' && 'text-right')}>
                    {col.sortKey ? (
                      <SortableHeader
                        label={col.label}
                        sortKey={col.sortKey}
                        currentSortKey={sortKey}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                      />
                    ) : (
                      col.label
                    )}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>

            <TableBody>
              {/* Loading state */}
              {isLoading && Array.from({ length: 10 }).map((_, i) => (
                <TableRow key={i}>
                  {visibleColumns.map((col) => (
                    <TableCell key={col.key}><Skeleton className="h-4 w-full" /></TableCell>
                  ))}
                </TableRow>
              ))}

              {/* Empty state */}
              {!isLoading && sortedItems.length === 0 && (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length} className="py-12 text-center text-muted-foreground">
                    No subscriptions found
                  </TableCell>
                </TableRow>
              )}

              {/* Data rows */}
              {!isLoading && sortedItems.map((contract) => (
                <TableRow
                  key={contract.id}
                  className="hover:bg-muted/50 cursor-pointer"
                  onClick={() => setEditingContractId(contract.id)}
                >
                  {visibleColumns.map((col) => (
                    <TableCell
                      key={col.key}
                      className={cn(
                        'text-sm',
                        col.align === 'right' && 'text-right tabular-nums',
                        col.className,
                      )}
                    >
                      {col.render
                        ? col.render(contract, rowHelpers)
                        : (String(col.getValue(contract) ?? '') || '—')}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

      </CardContent>

      {/* Create Subscription Modal */}
      <CreateSubscriptionModal
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
        onCreated={handleContractCreated}
        initialCustomerId={accountId}
        initialCustomerName={dashboardData?.account?.name}
        initialCustomerAccountNumber={dashboardData?.account?.account_number}
        initialSalesManagerId={dashboardData?.current_contract?.sales_manager_id}
      />

      {/* Reassign Dialog */}
      <ReassignDialog
        entityType="contract"
        entityId={reassignContract?.id ?? ''}
        entityLabel={reassignContract?.contractNumber ?? ''}
        currentAccountId={accountId}
        currentAccountName={accountName}
        open={!!reassignContract}
        onOpenChange={(open) => { if (!open) setReassignContract(null); }}
      />

      {/* Subscription Detail Modal */}
      <SubscriptionFormModal
        open={!!editingContractId}
        onOpenChange={(open) => { if (!open) setEditingContractId(null); }}
        contract={editingContractId ? editContractQuery.data || null : null}
        onSubmit={handleUpdateContract}
        isSubmitting={updateMutation.isPending}
      />
    </Card>
  );
}
